/* eslint-disable max-lines */
import { action, computed, extendObservable, flow, observable } from 'mobx';
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore, Store } from 'bernie-plugin-mobx';

import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { PageData } from 'bernie-http';
import { SerializedData, timeoutPromise } from 'bernie-core';

import { ILocalization } from '@shared-ui/localization-context';

import { BexApiSource } from 'sources/bex-api-source';

import { adaptPropertySearch, getPropertySearchInput } from 'stores/adapters';
import { mapOmnitureData } from 'stores/adapters/bex-api/search/search-results-adapter';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { STORE_EVENTS } from 'stores/events/store-events';
import { UIPageStore } from 'stores/ui-page-store';

import { DefaultRequestFields, LoyaltyMessagingModel, OverFilteredModel } from 'models';
import { GraphQLError } from 'graphql-request/dist/types';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import {
  LodgingEGDSPlainTextFragment,
  LodgingMoreInfoTriggerFragment,
  LodgingPwaPropertySearch,
  LodgingPwaPropertySearchQuery,
  OperationResult,
  PaginationInput,
} from '__generated__/api';
import { v4 as uuid } from 'uuid';

type PendingSearchResultsFetchFlow = ReturnType<SearchResultsStore['createSearchResultsFetchFlow']> | undefined | null;

export class SearchResultsStore extends Store {
  // Experiment 44305
  private static EXPERIMENT_V1_INITIAL_LISTING_FETCH_COUNT = 20;
  private static EXPERIMENT_V1_SECONDARY_LISTING_FETCH_COUNT = 0;
  private static EXPERIMENT_V2_INITIAL_LISTING_FETCH_COUNT = 5;
  private static EXPERIMENT_V2_SECONDARY_LISTING_FETCH_COUNT = 95;

  public static INITIAL_HOTEL_RENDER_COUNT = 50;
  private static PROPERTIES_FETCH_COUNT = 50;
  public static HOTEL_TOTAL_RENDER_COUNT = 50;

  private pendingSearchResultsFetchFlow: PendingSearchResultsFetchFlow;

  constructor(private bexApiSource: BexApiSource, state: SerializedData, logger: Logger = NOOP_LOGGER, l10n: ILocalization) {
    super(state, logger);

    this.searchCriteria = new SearchCriteriaModel({}, l10n);

    this.fetch = this.fetch.bind(this);
    this.searchProperties = this.searchProperties.bind(this);
  }

  public hydrate(data: SerializedData): void {
    delete data.source;
    delete data.bexApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    this.syncSearchDestination();
    delete data.searchCriteria;

    extendObservable(this, data);
  }

  public static DEFAULTS = {
    expandForm: false,
    foregoClientFetch: false,
    propertyListingAdaptexAnalyticsSuccessEvents: [],
    propertySearchListings: [],
    hotelsMaxDisplay: SearchResultsStore.INITIAL_HOTEL_RENDER_COUNT,
    isLoading: true,
    lodgingPriceBreakDown: {},
    loyaltyMessagingModel: undefined,
    messages: [],
    omnitureData: {},
    overFilteredModel: undefined,
    pageData: {},
    searchedDestination: '',
    shouldFetch: true,
    totalPropertyCount: 0,
    trackedTraces: {},
    isPaginationLoading: false,
  };

  @observable
  public error!: boolean | null;
  @observable
  public errors!: GraphQLError[] | undefined;
  @observable
  public expandForm!: boolean; // Set on client.
  @observable
  public foregoClientFetch!: boolean;
  @observable
  public propertySearchListings!: LodgingPwaPropertySearch.propertySearchListings[];
  @observable
  public propertyListingAdaptexAnalyticsSuccessEvents!: LodgingPwaPropertySearch.propertyListingAdaptexAnalyticsSuccessEvents[];
  @observable
  public hotelsMaxDisplay!: number;
  @observable
  public isLoading!: boolean;
  @observable
  public loyaltyMessagingModel!: LoyaltyMessagingModel;
  @observable
  public omnitureData: any;
  @observable
  public overFiltered!: boolean;
  @observable
  public overFilteredModel!: OverFilteredModel;
  @observable
  public searchedDestination!: string;
  @observable
  public shouldFetch!: boolean;
  @observable
  public resultMessages?: (LodgingPwaPropertySearch.resultMessages &
    LodgingEGDSPlainTextFragment.fragment &
    LodgingMoreInfoTriggerFragment.fragment)[];
  @observable
  public totalPropertyCount!: number;
  @observable.ref
  public pageData!: PageData;
  @observable.ref
  public searchCriteria!: SearchCriteriaModel;
  @observable
  public shoppingContextMultiItemId!: string | undefined;
  @observable
  public errorResponse!: LodgingPwaPropertySearch.errorResponse;
  @observable
  public dialogErrorResponse!: LodgingPwaPropertySearch.dialogErrorResponse;
  @observable
  public trackedTraces!: Record<string, number>;
  @observable
  public pagination!: LodgingPwaPropertySearch.pagination;
  @observable
  public mapTrigger!: LodgingPwaPropertySearch.mapTrigger;
  @observable
  public isPaginationLoading!: boolean;

  public resultsHeading!: string;
  public defaultRequestFields!: DefaultRequestFields;
  public maxHotelsDisplayedPerLoad: number = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  public prefetchedMultiItemSession = false;
  public initialLoadSearchPagination: Record<string, number> | undefined = undefined;

  @computed
  get isError(): boolean {
    return Boolean(
      !this.isLoading && (this.error || this.errors?.length || this.overFiltered || this.errorResponse || this.dialogErrorResponse)
    );
  }

  @computed
  get hasAtLeastOneProperty(): boolean {
    return this.propertySearchListings?.length > 0;
  }

  @action
  public showNextPage(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    multiItem: MultiItemStore,
    uiPage: UIPageStore,
    experiments?: ExperimentStore
  ) {
    if (!this.pagination?.subSets?.nextSubSet) return;

    const { startingIndex } = this.pagination?.subSets?.nextSubSet || {};
    const pagInput: PaginationInput = {
      startingIndex,
      size: SearchResultsStore.PROPERTIES_FETCH_COUNT,
    };

    this.showItemsByPage(analyticsStore, context, multiItem, uiPage, pagInput, experiments);
  }

  @action
  public showPreviousPage(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    multiItem: MultiItemStore,
    uiPage: UIPageStore,
    experiments?: ExperimentStore
  ) {
    if (!this.pagination?.subSets?.previousSubset) return;

    const { startingIndex } = this.pagination?.subSets?.previousSubset || {};
    const pagInput: PaginationInput = {
      startingIndex,
      size: SearchResultsStore.PROPERTIES_FETCH_COUNT,
    };

    this.showItemsByPage(analyticsStore, context, multiItem, uiPage, pagInput, experiments);
  }

  public showItemsByPage(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    multiItem: MultiItemStore,
    uiPage: UIPageStore,
    paginationInput: PaginationInput,
    experiments?: ExperimentStore,
    showProgressIndicator = true
  ) {
    const multiItemContext = multiItem?.multiItemContext ?? null;
    const propertySearchFetch = this.searchProperties(
      context,
      multiItemContext,
      this.searchCriteria,
      this.pageData.pageId || '',
      paginationInput,
      experiments
    );
    this.isPaginationLoading = true;
    if (showProgressIndicator) {
      this.showProgressIndicator(uiPage);
    }
    if (this.pagination?.trigger?.__typename === 'LodgingPaginationNavigationButtons') {
      this.propertySearchListings = [];
    }

    propertySearchFetch
      .then(({ data, errors, extensions }) => {
        this.errors = errors ?? [];

        this.omnitureData = mapOmnitureData(extensions.analytics);

        if (data?.propertySearch.pagination) {
          this.pagination = data?.propertySearch.pagination;
        }

        if (multiItem) {
          multiItem.updateSessionId(this.getMultiItemId(data));
        }

        if (data?.propertySearch.summary.resultMessages) {
          this.resultMessages = data?.propertySearch.summary.resultMessages as (LodgingPwaPropertySearch.resultMessages &
            LodgingEGDSPlainTextFragment.fragment &
            LodgingMoreInfoTriggerFragment.fragment)[];
        }

        if (data?.propertySearch.pagination?.trigger?.__typename === 'LodgingPaginationNavigationButtons') {
          this.propertySearchListings = data?.propertySearch.propertySearchListings ?? [];

          return;
        }

        this.propertySearchListings = [...this.propertySearchListings, ...(data?.propertySearch.propertySearchListings ?? [])];
      })
      .catch(error => {
        this.logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_FETCH_ERROR, getErrorString(error));
      })
      .finally(() => {
        // isPaginationLoading needs to change with new omniture data in one step
        this.isPaginationLoading = false;
        if (showProgressIndicator) {
          this.hideProgressIndicator(uiPage);
        }
      });

    analyticsStore?.publishClientSidePayload([this.omnitureData]);
  }

  private numberOfListingsFetched() {
    const lodgingCardCount = this.propertySearchListings.filter(listing => listing.__typename === 'LodgingCard').length;

    if (lodgingCardCount && lodgingCardCount > 0) {
      return lodgingCardCount;
    }

    return this.propertySearchListings.length;
  }

  @computed
  public get thereAreMoreListingsToFetch() {
    return this.numberOfListingsFetched() < this.totalPropertyCount;
  }

  // TODO: clean REDUCED_NUMBER_OF_SRP_LISTINGS experiment related code
  public static REDUCED_NUMBER_OF_SRP_LISTINGS_INITIAL_FETCH_COUNT_BY_BUCKET(bucket: number) {
    switch (bucket) {
      case 1:
        return SearchResultsStore.EXPERIMENT_V1_INITIAL_LISTING_FETCH_COUNT;
      case 2:
        return SearchResultsStore.EXPERIMENT_V2_INITIAL_LISTING_FETCH_COUNT;
      default:
        return 0;
    }
  }

  public static REDUCED_NUMBER_OF_SRP_LISTINGS_SECONDARY_FETCH_COUNT_BY_BUCKET(bucket: number) {
    switch (bucket) {
      case 1:
        return SearchResultsStore.EXPERIMENT_V1_SECONDARY_LISTING_FETCH_COUNT;
      case 2:
        return SearchResultsStore.EXPERIMENT_V2_SECONDARY_LISTING_FETCH_COUNT;
      default:
        return 0;
    }
  }

  public isFetchSecondaryListingBatch(experimentBucket: number) {
    if (
      this.numberOfListingsFetched() === 0 ||
      SearchResultsStore.REDUCED_NUMBER_OF_SRP_LISTINGS_SECONDARY_FETCH_COUNT_BY_BUCKET(experimentBucket) === 0
    ) {
      return false;
    }

    const initialFetchCount = SearchResultsStore.REDUCED_NUMBER_OF_SRP_LISTINGS_INITIAL_FETCH_COUNT_BY_BUCKET(experimentBucket);

    return this.thereAreMoreListingsToFetch && this.numberOfListingsFetched() <= initialFetchCount;
  }

  hideProgressIndicator(uiPage?: UIPageStore) {
    uiPage?.hideProgressIndicator();
  }
  showProgressIndicator(uiPage?: UIPageStore) {
    uiPage?.showProgressIndicator();
  }

  @action
  public updateSearchCriteria(searchCriteria: SearchCriteriaModel): void {
    this.searchCriteria = searchCriteria;
    this.syncSearchDestination();
    this.shouldFetch = true;
  }

  @action
  public createSessionBeforeMultiItemFetch(
    multiItem: MultiItemStore,
    context: ContextStore,
    incomingSearchCriteria: SearchCriteriaModel
  ): Promise<any> {
    this.isLoading = true;
    this.shouldFetch = false;
    this.propertySearchListings = [];
    this.prefetchedMultiItemSession = true;

    return multiItem
      .createMultiItemSession(context, incomingSearchCriteria)
      .then(misId => (incomingSearchCriteria.misId = misId))
      .catch(() => {
        this.isLoading = false;
        this.shouldFetch = true;
        this.prefetchedMultiItemSession = false;
        this.error = true;
      });
  }

  @action
  public fetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    timeout: number,
    additionalCriteria: SerializedData,
    multiItem: MultiItemStore,
    page: PageStore,
    experiments?: ExperimentStore,
    includePropertyListing?: boolean
  ): Promise<any> {
    if (!this.shouldFetch && !this.prefetchedMultiItemSession) {
      return Promise.resolve(this);
    }
    // cancel previous search fetch promise to prevent a race condition
    this.pendingSearchResultsFetchFlow?.cancel();
    this.pendingSearchResultsFetchFlow = this.createSearchResultsFetchFlow(
      analyticsStore,
      context,
      timeout,
      additionalCriteria,
      multiItem,
      page,
      experiments,
      includePropertyListing
    );

    return this.pendingSearchResultsFetchFlow;
  }

  /*
   * The flow wrapper is an optional alternative to async / await that makes it easier
   * to work with MobX actions and allows the promise returned to be cancelled.
   * https://mobx.js.org/actions.html#using-flow-instead-of-async--await-
   */
  //
  @action
  private createSearchResultsFetchFlow = flow(
    function* (
      analyticsStore: AnalyticsStore,
      context: ContextStore,
      timeout: number,
      additionalCriteria: SerializedData = {},
      multiItem: MultiItemStore,
      page: PageStore,
      experiments?: ExperimentStore,
      includePropertyListing?: boolean
    ): Generator<Promise<OperationResult<LodgingPwaPropertySearchQuery>[]>> {
      // @ts-expect-error self alias required for mobx flow syntax and typescript compatibility
      // eslint-disable-next-line @typescript-eslint/no-this-alias,@typescript-eslint/ban-ts-comment,consistent-this
      const self = this as SearchResultsStore;
      self.prefetchedMultiItemSession = false;
      self.shouldFetch = false;
      self.isLoading = true;
      self.error = null;
      self.errors = [];
      self.propertySearchListings = [];

      const multiItemContext = multiItem ? multiItem.multiItemContext : null;

      self.searchCriteria.searchId = uuid();

      const criteriaJson = self.searchCriteria.toJSON();
      const bexApiCriteria = { ...criteriaJson, ...additionalCriteria };

      const propertySearchFetch: Promise<OperationResult<LodgingPwaPropertySearch.query>> = timeoutPromise(
        self.searchProperties(
          context,
          multiItemContext,
          bexApiCriteria,
          page.pageId,
          self.initialLoadSearchPagination,
          experiments,
          includePropertyListing
        ),
        timeout,
        'propertySearchFetch timeout error'
      );

      try {
        const [bexApiResults] = (yield Promise.all([propertySearchFetch])) as OperationResult<LodgingPwaPropertySearchQuery>[];

        if (multiItem && bexApiResults) {
          const shoppingContextMultiItemId = self.getMultiItemId(bexApiResults.data);
          multiItem.updateSessionId(shoppingContextMultiItemId);
          self.shoppingContextMultiItemId = shoppingContextMultiItemId;
        }

        self.errors = bexApiResults.errors;

        const data = adaptPropertySearch(bexApiResults);

        self.pendingSearchResultsFetchFlow = null;

        if (data) {
          self.setOverFilteredModel(data.overFilteredModel);

          const searchCriteria = self.searchCriteria.clone();
          const defaultRequestFields = data.defaultRequestFields;

          if (defaultRequestFields) {
            Object.assign(searchCriteria, defaultRequestFields);
          }

          self.searchCriteria = searchCriteria;

          Object.assign(self, data);

          analyticsStore.publishClientSidePayload([self.omnitureData]);
        }

        self.isLoading = false;

        return data;
      } catch (err) {
        self.logErrorEvent(STORE_EVENTS.FETCH_ERROR, err);
        // don't assign the error to this.error, as errors in this catch are technical and NOT user-friendly
        self.error = true;
        self.isLoading = false;
        self.shouldFetch = true;

        // recover and pass object with error details
        return { error: err };
      }
    }.bind(this)
  );

  private searchProperties(
    context: ContextStore,
    multiItemContext: MultiItemContext | null,
    combinedCriteria: any,
    pageId: string,
    searchPagination?: PaginationInput,
    experiments?: ExperimentStore,
    includePropertyListing?: boolean
  ): Promise<OperationResult<LodgingPwaPropertySearch.query>> {
    const variables: LodgingPwaPropertySearch.Variables = getPropertySearchInput(
      context,
      combinedCriteria,
      multiItemContext,
      searchPagination,
      includePropertyListing
    );

    return this.bexApiSource.searchProperties(variables, pageId || '');
  }

  private syncSearchDestination() {
    if (this.searchCriteria.destination && this.searchCriteria.destination !== '') {
      this.searchedDestination = this.searchCriteria.destination;
    } else if (this.searchedDestination && this.searchedDestination !== '') {
      this.searchCriteria.destination = this.searchedDestination;
    }
  }

  private logErrorEvent(event: any, err: any) {
    if (this.logger) {
      this.logger.logEvent(event, getErrorString(err));
    }
  }

  // Your editor may be telling you that this is unused. It is lying to you. Don't trust it.
  // In createSearchResultsFetchFlow we create a reference to `this` and call `self.setOverFilteredModel`.
  private setOverFilteredModel(overFilteredModel: OverFilteredModel | undefined) {
    if (this.overFilteredModel && !overFilteredModel) {
      this.overFilteredModel = {};
    }
  }

  @action
  public clearDefaultRequestFields() {
    this.defaultRequestFields = {};
  }

  @action
  public setHotelsMaxDisplayToTotalRenderCount() {
    this.hotelsMaxDisplay = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  }

  @action
  public setExpandForm(shouldExpandForm: boolean) {
    this.expandForm = shouldExpandForm;
  }

  public getPageLoadNumber() {
    return this.hotelsMaxDisplay / this.maxHotelsDisplayedPerLoad;
  }

  private getMultiItemId(data?: LodgingPwaPropertySearch.query) {
    return data?.propertySearch?.shoppingContext?.multiItem?.id;
  }
}
